import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Meter } from "@/components/ui/chart";
import { Callout, ErrorState } from "@/components/ui/feedback";
import { abandonStatus, countLine, percent } from "@/lib/abandonment";
import { useCampaignAbandonment } from "@/lib/queries";
import type { Campaign } from "@/lib/types";

/**
 * The campaign's abandon rate against the abandonment rule (platform ADR-206), said where its
 * dialing is set. Over the campaign's preview and power dials of the last 30 days, the calls a
 * person answered that no rep reached within the wait, over the live answers. Shown only: the
 * dialer never stops the campaign on it.
 */
export function CampaignAbandonment({ campaign }: { campaign: Campaign }) {
  const { data, error, refetch } = useCampaignAbandonment(campaign.id);

  const description =
    `Calls a person answered that no rep reached within ${campaign.abandon_after_seconds} ` +
    `second${campaign.abandon_after_seconds === 1 ? "" : "s"}, over this campaign's preview and ` +
    "power dials in the last 30 days. The legal limit is 3% of the live answers. Machines the " +
    "detection heard and numbers dialed by hand are not counted. Dialing never stops on it.";

  if (error) {
    return (
      <Card>
        <CardHeader title="Abandon rate" description={description} />
        <CardBody>
          <ErrorState
            error={error}
            title="Could not load the abandon rate"
            action={
              <Button type="button" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  }

  const status = data ? abandonStatus(data) : null;

  return (
    <Card>
      <CardHeader title="Abandon rate" description={description} />
      <CardBody className="space-y-4">
        {status && status.tone !== "ok" && (
          <Callout tone={status.tone}>
            <span>
              <span className="font-semibold">{status.title}.</span> {status.detail}
            </span>
          </Callout>
        )}
        {data && (
          <div className="grid items-center gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
            <Meter
              value={data.abandon_rate}
              max={0.1}
              dangerAt={data.limit}
              limit={data.limit}
              label={`${campaign.name} abandon rate`}
            />
            <span className="flex items-center gap-1.5 text-sm tabular-nums">
              {data.over_limit && (
                <ShieldAlert className="size-3.5 text-warning-11" aria-label="Over the limit" />
              )}
              <span className={data.over_limit ? "font-semibold text-warning-11" : "font-semibold text-text"}>
                {percent(data.abandon_rate)}
              </span>
              <span className="text-xs text-text-subtle">{countLine(data)}</span>
            </span>
          </div>
        )}
        {status?.tone === "ok" && <p className="text-xs text-text-muted">{status.detail}</p>}
      </CardBody>
    </Card>
  );
}
